'use client';

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import useAnalytics from 'src/hooks/analytics/useAnalytics';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faStethoscope,
  faRightFromBracket,
} from '@fortawesome/pro-solid-svg-icons';
import { faGlobe, faChartLine } from '@fortawesome/pro-regular-svg-icons';
import { TableRowActions, PulseBadge } from '../';
import {
  Tooltip,
  PulseHeader,
  PulseCell,
  PulseColumnMenu,
  PulseColumnDef,
  PulseLoading,
} from 'src/components';
import { ColumnActionsSection } from './ColumnActionsSection';
import { SortSection } from '../AnalyticsTrendsTable/SortSection';
import { AnalyticsErrorOverlay } from '../AnalyticsErrorOverlay';
import { AnalyticsTableHeader } from '../AnalyticsTableHeader';
import { ANALYTICS_LOCATION } from 'src/hooks/filters/constants';
import {
  useCreateBulkExportV2Mutation,
  useGetCodeSetAnalyticsQuery,
} from 'src/api/redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { toApiPeriod } from 'src/utils/quarterUtils';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useAuth } from 'src/context/auth';
import { getNewExport } from 'src/api';
import { AnalyticsResultItem } from '../types';
import { ANALYTICS_DEFAULT_DATE_RANGE } from 'src/components/constants';

// Type for the code set data returned from API
type CodeSetResultItem = AnalyticsResultItem<{
  claims?: Record<string, number>;
  patients?: Record<string, number>;
}>;

const AnalyticsCodeSetsTable = () => {
  const { user } = useAuth();
  const auth = useAuthorization();
  const isMedscout = user?.is_superuser || user?.permissions?.is_support_user;
  const userCanBulkExport =
    isMedscout || auth.hasPermission('export.providers_in_territory.all');
  const log = useLogging();
  const { setNotification } = useNotification();
  const { closeAllButCurrent } = useDrawer();
  // Redux state and actions (CODE_SETS location)
  const {
    selectedGeographyObjects = [],
    selectedCodeSet = [],
    formattedFiltersForAPI,
    date_range = ANALYTICS_DEFAULT_DATE_RANGE,
    granularity = 'quarter',
    periods = [],
    volumeTypes = [],
    modeType,
    volumeConfigs = {},
    updateVolumeConfig,
    removeVolumeConfig,
    setSelectedCodeSet,
  } = useAnalytics(ANALYTICS_LOCATION.CODE_SETS) || {};

  // Under the `quarterly-ta-data` flag, send the granularity + periods contract.
  // `granularity` lives INSIDE `date_range` (it's a field of DateRangeSerializer)
  // — a top-level granularity is ignored by the backend and silently collapses
  // results to year buckets. Otherwise keep the legacy gte/lte year range.
  const { quarterlyTaData } = useFlags();
  const timeFilter = useMemo(() => {
    if (quarterlyTaData) {
      return {
        date_range: {
          granularity,
          periods: periods.map(toApiPeriod),
        },
      };
    }
    return {
      date_range: {
        gte: parseInt(date_range.gte),
        lte: parseInt(date_range.lte),
      },
    };
  }, [quarterlyTaData, granularity, periods, date_range]);

  // Bulk export mutation
  const [createBulkExportV2] = useCreateBulkExportV2Mutation();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [sort, setSort] = useState<{
    sort: string;
    order: 'asc' | 'desc';
  } | null>({
    sort: 'name',
    order: 'asc',
  });

  useEffect(() => {
    if (sort && sort.sort !== 'name') {
      const currentVolumeType = volumeTypes?.[0] || 'claims';
      if (!sort.sort.includes(currentVolumeType)) {
        setSort({
          sort: 'name',
          order: 'asc',
        });
      }
    }
  }, [volumeTypes, sort]);

  const shouldFetch = useMemo(() => {
    if (!selectedCodeSet) return false;
    return Array.isArray(selectedGeographyObjects) && selectedGeographyObjects.length > 0 && selectedCodeSet?.length > 0;
  }, [selectedGeographyObjects, selectedCodeSet]);

  // Build volume_configs for API - Code Sets table can have multiple code sets
  const apiVolumeConfigs = useMemo(() => {
    if (!selectedCodeSet || selectedCodeSet.length === 0) return {};

    return selectedCodeSet.reduce((acc, codeSet) => {
      const instanceKey = codeSet.instanceId.toString();

      // Use existing volumeConfig if available, otherwise create default config
      // Use the mode from the codeSet object (set when selected), or from volumeConfigs, or default to treating
      const config = volumeConfigs[instanceKey] || {
        code_set: codeSet.id,
        mode: codeSet.mode || modeType || 'treating',
      };

      acc[instanceKey] = config;
      return acc;
    }, {} as Record<string, any>);
  }, [selectedCodeSet, volumeConfigs, volumeTypes, date_range, modeType]);

  const {
    data: codeSetData,
    isLoading: isLoadingCodeSets,
    isFetching: isFetchingCodeSets,
    isError: isErrorCodeSets,
    error: errorCodeSets,
  } = useGetCodeSetAnalyticsQuery(
    {
      params: {
        sort: sort?.sort,
        order: sort?.order,
      },
      data: {
        volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
        ...timeFilter,
        volume_configs: apiVolumeConfigs,
        geographies: selectedGeographyObjects.map((g) => ({
          id: g.id,
          type: g.type,
        })),
        ...(formattedFiltersForAPI.length > 0 && {
          filters: formattedFiltersForAPI,
        }),
      },
    },
    {
      skip: !shouldFetch,
    }
  );

  // Handle bulk export with provider or site type
  const handleBulkExport = async (type: 'provider' | 'site', id: string) => {
    log.event('Export initiated', {
      source: 'AnalyticsCodeSetsTable',
      exportType: type,
    });

    const geographyToSend = selectedGeographyObjects.find(
      (geo) => geo.id.toString() === id
    );
    if (!geographyToSend) {
      setNotification({
        title: 'Error',
        message: 'Selected geography not found for export.',
        type: 'error',
      });
      return;
    }
    try {
      await createBulkExportV2({
        data: {
          volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
          ...timeFilter,
          volume_configs: apiVolumeConfigs,
          geographies: [
            {
              type: geographyToSend.type,
              id: geographyToSend.id,
            },
          ],
          ...(formattedFiltersForAPI.length > 0 && {
            filters: formattedFiltersForAPI,
          }),
        },
        type,
      }).unwrap();
      setNotification({
        title: 'Success',
        message:
          'Export requested, you will receive an email when it is ready!',
        type: 'success',
      });
    } catch (err) {
      log.exception(`Error exporting territory analytics (${type}): ${err}`);

      setNotification({
        title: 'Error',
        message: err?.data?.error_message || 'Error requesting export',
        type: 'error',
      });
    }
  };

  const { dataColumns, dataKeys } = useMemo(() => {
    return {
      dataColumns: selectedCodeSet.map((cs) => cs.label) || [],
      dataKeys: selectedCodeSet.map((cs) => cs.instanceId) || [],
    };
  }, [selectedCodeSet]);

  // Define columns using PulseColumnDef for resizing (excluding Geography which is rendered separately)
  const columns: PulseColumnDef[] = useMemo(() => {
    return dataKeys.map((key, i) => ({
      field: key.toString(),
      headerName: dataColumns[i],
      width: 200,
      minWidth: 150,
      maxWidth: 400,
      resizable: true,
      sortable: true,
    }));
  }, [dataKeys, dataColumns]);

  // Use column resizing hook
  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns });

  const displayedGeographies = useMemo((): CodeSetResultItem[] => {
    const geographies = Array.isArray(selectedGeographyObjects) ? selectedGeographyObjects : [];

    if (codeSetData?.results && codeSetData.results.length > 0) {
      const selectedIds = new Set<string | number>(
        geographies.map((g) => g.id)
      );

      const sortedResults = codeSetData.results.filter(
        (result: CodeSetResultItem) => selectedIds.has(result.id)
      );

      const resultsMap = new Map<string | number, CodeSetResultItem>(
        codeSetData.results.map((result: CodeSetResultItem) => [
          result.id,
          result,
        ])
      );

      const missingGeographies = geographies
        .filter((geography) => !resultsMap.has(geography.id))
        .map(
          (geography): CodeSetResultItem => ({
            id: geography.id,
            name: geography.name || '',
            type: geography.type,
          })
        );

      return [...sortedResults, ...missingGeographies];
    }

    return geographies.map(
      (geography): CodeSetResultItem => ({
        id: geography.id,
        name: geography.name || '',
        type: geography.type,
      })
    );
  }, [codeSetData, selectedGeographyObjects]);

  // Handle table export
  const handleExport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      try {
        // Build query params for the export API
        const queryParams = new URLSearchParams({
          sort: sort?.sort || 'name',
          order: sort?.order || 'asc',
        });

        await getNewExport({
          url: `https://api.medscout.io/api/v1/analytics/territory-insights/code-set/?${queryParams.toString()}`,
          method: 'POST',
          fileFormat: format,
          columns: ['id', 'name', 'type', `${volumeTypes?.[0]}`],
          fileName: `analytics-total-volume-${
            new Date().toISOString().split('T')[0]
          }`,
          data: {
            volume_types: volumeTypes.length > 0 ? volumeTypes : ['claims'],
            date_range: {
              gte: parseInt(date_range.gte),
              lte: parseInt(date_range.lte),
            },
            volume_configs: apiVolumeConfigs,
            geographies: selectedGeographyObjects.map((g) => ({
              id: g.id,
              type: g.type,
            })),
            ...(formattedFiltersForAPI.length > 0 && {
              filters: formattedFiltersForAPI,
            }),
          },
        });

        setNotification({
          title: 'Success',
          message:
            'Export initiated - You will receive an email when it is ready for download',
          type: 'success',
        });

        log.event('Export', {
          type: ANALYTICS_LOCATION.CODE_SETS,
          source: 'Analytics Total Volume Table',
          columns: columns.map((col) => col.field),
          count: displayedGeographies.length,
          format,
        });
      } catch (err) {
        log.exception(`Error exporting total volume: ${err}`);

        setNotification({
          title: 'Error',
          message: err?.error_message || 'Error exporting, please try again.',
          type: 'error',
        });
      }
    },
    [
      columns,
      volumeTypes,
      date_range,
      modeType,
      selectedGeographyObjects,
      formattedFiltersForAPI,
      sort,
      log,
      setNotification,
      displayedGeographies,
    ]
  );

  const handleOpenTerritoryDrawer = useCallback(() => {
    log.event('Territory drawer opened', {
      source: 'AnalyticsCodeSetsTable',
    });
    closeAllButCurrent('analytics-territory-drawer');
  }, [closeAllButCurrent, log]);

  const handleEllipsisClick = useCallback(
    (e: React.MouseEvent, codeSetId: string) => {
      e.stopPropagation();
      const isOpening = openMenuId !== codeSetId;
      if (isOpening) {
        log.event('Column menu opened', {
          source: 'AnalyticsCodeSetsTable',
          columnId: codeSetId,
        });
      }
      setOpenMenuId((prev) => (prev === codeSetId ? null : codeSetId));
    },
    [openMenuId, log]
  );

  const handleRemoveColumn = useCallback(
    (instanceKey: string) => {
      const codeSet = selectedCodeSet.find(
        (cs) =>
          (cs.instanceId ? cs.instanceId : cs.id.toString()) === instanceKey
      );

      log.event('Column removed', {
        source: 'AnalyticsCodeSetsTable',
        instanceKey,
        codeSetLabel: codeSet?.label,
      });

      // Remove this specific instance from selectedCodeSet
      const updatedCodeSets = selectedCodeSet.filter(
        (cs) =>
          (cs.instanceId ? cs.instanceId : cs.id.toString()) !== instanceKey
      );
      setSelectedCodeSet(updatedCodeSets);

      // Also remove its volume config
      removeVolumeConfig(instanceKey);

      setOpenMenuId(null);
    },
    [selectedCodeSet, setSelectedCodeSet, removeVolumeConfig, log]
  );

  const handleSortToggle = useCallback(
    (columnName: string, order?: 'asc' | 'desc') => {
      setSort((prevSort) => {
        const newOrder =
          order ||
          (prevSort?.sort === columnName
            ? prevSort.order === 'asc'
              ? 'desc'
              : 'asc'
            : 'desc');
        log.event('Sort changed', {
          source: 'AnalyticsCodeSetsTable',
          columnName,
          sortOrder: newOrder,
        });
        if (order) {
          return {
            sort: columnName,
            order,
          };
        }
        if (prevSort?.sort === columnName) {
          return {
            sort: columnName,
            order: prevSort.order === 'asc' ? 'desc' : 'asc',
          };
        }
        return {
          sort: columnName,
          order: 'desc',
        };
      });
      setOpenMenuId(null);
    },
    [log]
  );

  const hasGeographies = Array.isArray(selectedGeographyObjects) && selectedGeographyObjects.length > 0;
  const hasCodeSets = selectedCodeSet?.length > 0;
  const showGeographyEmptyState = !hasGeographies;
  const showCodeSetEmptyState = !hasCodeSets;
  const showLoadingOverlay =
    (isLoadingCodeSets || isFetchingCodeSets) && hasGeographies && hasCodeSets;

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden bg-white relative rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.00)] border border-neutral-200"
      data-testid="analytics-code-sets-table"
    >
      <AnalyticsTableHeader
        columns={dataColumns}
        showModeSelector={false}
        location={ANALYTICS_LOCATION.CODE_SETS}
        allowMultiple
        onExport={handleExport}
      />

      <div
        className={`flex-1 relative ${
          hasGeographies
            ? 'overflow-x-auto overflow-y-auto'
            : 'overflow-hidden'
        }`}
        data-testid="code-sets-table-container"
      >
        {/* Full-height separator line between Geography and data columns */}
        {(hasGeographies || showGeographyEmptyState) && (
          <div className="sticky left-[271px] top-12 h-[calc(100%-3rem)] w-px bg-neutral-200 z-[21] pointer-events-none float-left -ml-px" />
        )}

        <div
          role="table"
          aria-label="Analytics code sets data by geography"
          className="grid relative z-10 w-full"
          style={{
            gridTemplateColumns:
              dataColumns.length === 0
                ? '272px 1fr'
                : `272px ${getGridTemplateColumns()}`,
            gridAutoFlow: 'row',
          }}
        >
          <PulseHeader
            label="Geography"
            isSticky
            stickyPosition="both"
            className="z-30 bg-neutral-50 text-neutral-900 border-b border-r border-b-neutral-200 border-r-neutral-200"
            sortable
            sortDirection={sort?.sort === 'name' ? sort.order : null}
            resizable={false}
            menu={
              <PulseColumnMenu
                columnId="name"
                isOpen={openMenuId === 'name'}
                onToggle={(e) => handleEllipsisClick(e, 'name')}
                customSectionComponents={[
                  <SortSection
                    key="sort"
                    currentOrder={sort?.order || null}
                    isActive={sort?.sort === 'name'}
                    onChange={(order) => handleSortToggle('name', order)}
                  />,
                ]}
              />
            }
          />

          {dataColumns.map((col, i) => {
            const instanceKey = dataKeys[i];
            const codeSet = selectedCodeSet[i];
            const isMenuOpen = openMenuId === instanceKey;
            const currentType =
              volumeConfigs[instanceKey]?.mode || codeSet?.mode || 'treating';
            const sortValue = `${volumeTypes?.[0] || 'claims'}.${instanceKey}`;
            const isActiveSortColumn = sort?.sort === sortValue;

            return (
              <PulseHeader
                key={instanceKey}
                label={
                  <Tooltip
                    content={codeSet?.name || col}
                    slotProps={{
                      containerClassName: '!bg-neutral-900 !text-white',
                    }}
                    className="!block cursor-pointer min-w-0 w-full"
                  >
                    <span className="truncate block">{col}</span>
                  </Tooltip>
                }
                endDecorator={
                  <PulseBadge className="px-2 py-0.5 bg-sky-600 rounded-2xl inline-flex justify-center items-center gap-0.5 overflow-hidden">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <Icon
                        size="xs"
                        className="text-white w-2.5 h-2.5"
                        icon={
                          currentType === 'treating'
                            ? faStethoscope
                            : faRightFromBracket
                        }
                      />
                    </div>
                    <div className="text-center justify-start text-white text-xs font-normal font-['Inter'] leading-4">
                      {currentType === 'treating' ? 'Treating' : 'Referring'}
                    </div>
                  </PulseBadge>
                }
                menu={
                  <PulseColumnMenu
                    columnId={instanceKey}
                    isOpen={isMenuOpen}
                    onToggle={(e) => handleEllipsisClick(e, instanceKey)}
                    customSectionComponents={[
                      <SortSection
                        key="sort"
                        currentOrder={sort?.order || null}
                        isActive={isActiveSortColumn}
                        onChange={(order) => handleSortToggle(sortValue, order)}
                      />,
                      <ColumnActionsSection
                        key="column-actions"
                        onRemove={() => handleRemoveColumn(instanceKey)}
                      />,
                    ]}
                  />
                }
                sortable
                sortDirection={isActiveSortColumn ? sort.order : null}
                resizable={true}
                onStartResize={(e) => startResizing(instanceKey.toString(), e)}
                isResizing={isResizing}
                isSticky
                stickyPosition="top"
                className="z-20 bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200"
              />
            );
          })}

          {/* Filler Column Header - empty cell to fill remaining space */}
          <div className="bg-neutral-50 border-b border-b-neutral-200 sticky top-0 z-20" />

          {/* Body of the table */}
          {showGeographyEmptyState && (
            <React.Fragment>
              <div className="flex sticky left-0 items-center justify-center w-[272px] h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Icon icon={faGlobe} className="text-2xl" />
                  <span>Select Geographies</span>
                  <button
                    type="button"
                    onClick={handleOpenTerritoryDrawer}
                    className="px-4 py-2 bg-white text-secondary border border-neutral-200 text-sm font-medium font-['Inter'] rounded-full hover:bg-neutral-100 transition-all duration-300"
                  >
                    Add Geographies
                  </button>
                </div>
              </div>
            </React.Fragment>
          )}
          {/* Code set empty state - State 1: Part of grid when no geographies */}
          {showGeographyEmptyState && showCodeSetEmptyState && (
            <div
              className="flex items-center justify-center bg-white h-[calc(100vh-18.75rem)] text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
              style={{ gridColumn: '2 / -1' }}
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <Icon icon={faChartLine} className="text-2xl" />
                <span>Select Code Sets to see code set data</span>
              </div>
            </div>
          )}

          {displayedGeographies.map((geography) => {
            const isRegion = geography.type === 'region';

            return (
              <React.Fragment key={geography.id}>
                <PulseCell
                  isSticky
                  stickyPosition="left"
                  className="sticky left-0 z-20 border-b border-r border-b-neutral-200 border-r-neutral-200 bg-white group"
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        data-testid={`geography-name-${geography.id}`}
                        className="truncate text-secondary text-sm font-normal font-['Inter'] leading-5"
                        title={geography.name}
                      >
                        {geography.name}
                      </span>
                      {isRegion && (
                        <Tooltip
                          content="This is a region"
                          slotProps={{
                            containerClassName: '!bg-neutral-900 !text-white',
                          }}
                        >
                          <span
                            className="inline-flex items-center text-xs text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full cursor-default shrink-0"
                            data-testid={`region-badge-${geography.id}`}
                            role="status"
                            aria-label="Region indicator"
                          >
                            Region
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <TableRowActions
                      geographyId={geography.id}
                      onBulkExport={
                        userCanBulkExport ? handleBulkExport : undefined
                      }
                      location={ANALYTICS_LOCATION.CODE_SETS}
                      isRegion={isRegion}
                    />
                  </div>
                </PulseCell>

              {dataKeys.map((key) => {
                const volumeData = geography?.[volumeTypes?.[0]] as
                  | Record<string, number>
                  | undefined;
                const value = volumeData?.[key];
                return (
                  <PulseCell
                    key={key}
                    className={`bg-white ${hasCodeSets ? 'border-b border-r border-b-neutral-200 border-r-neutral-200' : ''}`}
                  >
                    {hasCodeSets
                      ? value != null
                        ? handleNumericValue(value)
                        : 'n/a'
                      : null}
                  </PulseCell>
                );
              })}

              {/* Filler Column Cell - empty cell to fill remaining space */}
              <div className={`bg-white ${hasCodeSets ? 'border-b border-b-neutral-200' : ''}`} />
            </React.Fragment>
            );
          })}
        </div>

        {/* Code Set Empty State Overlay - State 2: Shows when we have geographies but no code sets */}
        {!showGeographyEmptyState && showCodeSetEmptyState && (
          <div
            className="absolute left-72 top-12 right-0 bottom-0 z-30 bg-white flex items-center justify-center text-neutral-500 text-sm font-medium font-['Inter'] leading-5"
            data-testid="code-set-empty-state-overlay"
          >
            <div className="flex flex-col items-center justify-center gap-2">
              <Icon icon={faChartLine} className="text-2xl" />
              <span>Select Code Sets to see code set data</span>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        <AnalyticsErrorOverlay
          error={errorCodeSets}
          isVisible={isErrorCodeSets && !isLoadingCodeSets && !isFetchingCodeSets}
        />

        {showLoadingOverlay && <PulseLoading />}
      </div>
    </div>
  );
};

export default AnalyticsCodeSetsTable;
